import * as React from 'react';
import * as classnames from 'classnames';
import { observer } from 'mobx-react';
import { ITranslationsCollection, translate } from 'elc-service';
import styled from 'styled-components';
import { Column, Row } from 'elc-grid';
import { ContentSpacing, breakpoint, Breakpoints } from 'elc-base-theme';
import { Body2, Caption, Heading5, Heading4 } from 'elc-typography';
import { Shade } from '../../domain/entities/Shade';
import {
    BackgroundGrayTheme,
    ShadeSliderPaginationTheme,
    ShadeSliderDotTheme,
    SidebarDetailsHeadingTheme,
    smooshImageTheme
} from '../../../theme/default-theme';
import { SliderView } from 'elc-slider';
import { Desktop, Mobile } from 'elc-layouts';
import { SIDEBAR_ADD_TO_BAG_QUANTITY } from '../../constants/ProductShadesSidebarConstants';
import { ProductShadesSidebarShadePicker } from './ProductShadesSidebarShadePicker';
import { IShadesByColorFamily } from '../../controllers/ProductShadesSidebarViewController';
import { InventoryStatusMessage } from '../common/InventoryStatusMessage';
import { ProductImages } from 'elc-images';
import { UndertoneLabel } from './UndertoneLabel';
import { ColorFamilyLabel } from './ColorFamilyLabel';
import { Sku } from '../../domain/entities/Sku';
import ProductCTA, { IProductCTAProps } from '../../../exported/product-cta/ProductCTA';
import { IProductShadesSidebarConfig } from './ProductShadesSidebar';
import { classes } from '../../utils/Classes';
import { ADD_TO_BAG } from '../../constants/CTATypes';
import { diContainer, serviceNames } from '../../../service-setup/diContainer';

interface IProductImage {
    src: string;
    alt: string;
    assetType?: string;
}

interface IImageWrapper {
    assetType?: string;
    selectedShade: Shade;
}

export interface IProductShadesSidebarContent {
    selectedShade: Shade;
    productFullAddToBagProps?: IProductCTAProps;
    translations?: ITranslationsCollection;
    onShadeSelect(name: string, productId: string): void;
    colorFamilies?: IShadesByColorFamily;
    modelAndSmooshImages: IProductImage[];
    shades: Shade[];
    config: IProductShadesSidebarConfig;
    selectedSku: Sku;
    autoReplenishOptions: number[];
    className?: string;
    largeImages: IProductImage[] | [];
}

const ContentWrapper = styled.div`
    width: 100%;
    height: 100%;
    max-height: 100vh;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    ${breakpoint('desktop')`
        flex-direction: row;
        [dir='rtl'] & {
            flex-direction: row-reverse;
        }
    `};
`;

const ShadeInfoWrapper = styled(Column)`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 0;
    ${breakpoint('desktop')`
        justify-content: flex-start;
    `};
`;

const ShadeDetails = styled.div`
    margin: ${ContentSpacing.space24};
    height: auto;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    ${breakpoint('desktop')`
        margin: 0;
    `};
`;

const ShadeDetailRow = styled(Row)`
    justify-content: space-between;
    ${breakpoint('desktop')`
        margin-bottom: ${ContentSpacing.space24};
    `};
`;

const ShadeDetailItem = styled.div`
    width: 50%;
`;

const ShadePickerWrapper = styled(Column)`
    padding: 0 0 ${ContentSpacing.space20};
    ${breakpoint('desktop')`
        overflow-y: scroll;
        ${BackgroundGrayTheme}
    `};
`;

const CtaButtonWrapper = styled.div`
    margin: ${ContentSpacing.space4};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    ${breakpoint('desktop')`
        margin: ${ContentSpacing.space16} ${ContentSpacing.space24};
    `};
`;

const StyledHeading = styled(Heading4)`
    ${SidebarDetailsHeadingTheme}
    margin-bottom: ${ContentSpacing.space24};
`;

const StyledMobileHeading = styled(Heading4)`
    ${SidebarDetailsHeadingTheme}
    margin-bottom: ${ContentSpacing.space24};
`;

const SliderWrapper = styled.div`
    margin: 0 ${ContentSpacing.space10};
    flex-grow: 1;
    height: 40%;

    & > div {
        height: 100%;
    }

    .slick-slider {
        height: 100%;
        margin-bottom: 0;
        * {
            height: 100%;
        }
    }
    .slick-dots {
        height: 60px;
        justify-content: center;
    }
    .slick-list {
        height: calc(100% - 60px);
    }
    .slick-dots li {
        width: auto;
        height: auto;
        justify-content: center;
    }
    .elc-img {
        width: auto;
        margin: auto;
    }
`;

const SliderPagination = styled.ul`
    display: flex;
    justify-content: flex-end;
    padding: ${ContentSpacing.space6} 0 ${ContentSpacing.space8};
    list-style: none;
    ${ShadeSliderPaginationTheme};

    [dir='rtl'] & {
        flex-direction: row-reverse;
    }

    ${breakpoint('desktop')`
        justify-content: center;
    `};
`;

const StyledSliderDot = styled.div`
    cursor: pointer;
    padding: ${ContentSpacing.space12} ${ContentSpacing.space8};

    .slick-active & {
        &::before {
            position: relative;
            top: -${ContentSpacing.space8};
        }
    }

    ${ShadeSliderDotTheme};
`;

const ImageWrapper = styled.div<IImageWrapper>`
    ${props =>
        props.assetType &&
        props.assetType === 'smoosh' &&
        smooshImageTheme(props.selectedShade.hexVal)}
`;

const StyledProductCTA = styled.div`
    margin-bottom: ${ContentSpacing.space24};
`;

const appendDots = (dots: React.ReactChildren) => {
    return <SliderPagination>{dots}</SliderPagination>;
};

const customPaging = (imageLabels: string[]) => {
    return (i: number) => {
        return (
            <StyledSliderDot>
                <Caption>{imageLabels[i]}</Caption>
            </StyledSliderDot>
        );
    };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sliderSettings: { [k: string]: any } = {
    dots: true,
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    responsive: [
        {
            breakpoint: Breakpoints.desktop,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1
            }
        }
    ],
    appendDots: appendDots
};

export const ProductShadesSidebarContent: React.FunctionComponent<IProductShadesSidebarContent> = translate(
    [
        'selectYourShadeLabel',
        'photoLabel',
        'shadeLabel',
        'previewLabel',
        'colorGroupLabel',
        'undertoneLabel'
    ]
)(
    observer((props: IProductShadesSidebarContent) => {
        const shadeInfoWrapperClassNames = classnames(
            'elc-product-shade-info-wrapper',
            'js-product-shade-info-wrapper'
        );

        const shadeDetailsWrapperClassNames = classnames(
            'elc-product-shade-details-wrapper',
            'js-product-shade-details-wrapper'
        );

        const shadesSidebarContentWrapperClassNames = classnames(
            'elc-product-shade-sidebar-content-wrapper',
            'js-product-shade-sidebar-content-wrapper'
        );

        const shadesSiderbarContentCTAClassNames = classes('shades-sidebar-content-cta');

        const shadeDetailRowClassNames = classes('shades-detail-row');

        const {
            selectedShade,
            productFullAddToBagProps,
            onShadeSelect,
            colorFamilies,
            shades,
            modelAndSmooshImages,
            config,
            selectedSku,
            autoReplenishOptions,
            largeImages
        } = props;

        const {
            selectYourShadeLabel,
            colorGroupLabel,
            undertoneLabel,
            photoLabel,
            shadeLabel
        } = props.translations as ITranslationsCollection;
        const shadeDetails = selectedShade && selectedShade.parentSku && (
            <ShadeDetailRow className={shadeDetailRowClassNames}>
                {selectedShade.parentSku.hasColorFamily && (
                    <ShadeDetailItem>
                        <Heading5>{colorGroupLabel}</Heading5>
                        <Body2>
                            {selectedShade.parentSku.colorFamily.map(cf => (
                                <ColorFamilyLabel
                                    key={`color-family-label-${cf.value}`}
                                    name={cf.value}
                                />
                            ))}
                        </Body2>
                    </ShadeDetailItem>
                )}
                {selectedShade.parentSku.hasUndertone && (
                    <ShadeDetailItem>
                        <Heading5>{undertoneLabel}</Heading5>
                        <Body2>
                            {selectedShade.parentSku.undertone.map(cf => (
                                <UndertoneLabel
                                    key={`undertone-label-${cf.value}`}
                                    name={cf.value}
                                />
                            ))}
                        </Body2>
                    </ShadeDetailItem>
                )}
            </ShadeDetailRow>
        );

        const shadeSidebarImages = modelAndSmooshImages.length ? modelAndSmooshImages : largeImages;

        const overlayImages = shadeSidebarImages.map(skuImage => (
            <ImageWrapper
                selectedShade={selectedShade}
                assetType={skuImage.assetType}
                key={`overlay-image-${skuImage.alt}`}
            >
                <ProductImages imgPath={skuImage.src} altText={skuImage.alt} />
            </ImageWrapper>
        ));

        sliderSettings.customPaging = customPaging([photoLabel, shadeLabel]);

        const inventoryStatus =
            selectedShade && selectedShade.parentSku ? selectedShade.parentSku.inventoryStatus : '';

        const displayInvMessage = !props.config.ctaType || props.config.ctaType === ADD_TO_BAG;

        const desktopCtaConfig = {
            skuId: selectedSku.skuId,
            quantity: SIDEBAR_ADD_TO_BAG_QUANTITY,
            ...productFullAddToBagProps,
            showSuccessLabel: true,
            ...config,
            autoReplenishOptions
        };

        const mobileCtaConfig = {
            ...desktopCtaConfig,
            hasStickyButtonVariation: true
        };

        return (
            <ContentWrapper className={shadesSidebarContentWrapperClassNames}>
                <ShadeInfoWrapper sm={8} className={shadeInfoWrapperClassNames}>
                    <SliderWrapper>
                        <SliderView sliderSettings={sliderSettings} items={overlayImages} />
                    </SliderWrapper>
                    <ShadeDetails className={shadeDetailsWrapperClassNames}>
                        <Desktop minWidth={Breakpoints.desktop}>
                            {() => (
                                <StyledHeading>
                                    {selectedShade ? selectedShade.name : selectYourShadeLabel}
                                </StyledHeading>
                            )}
                        </Desktop>
                        <Mobile maxWidth={Breakpoints.desktop - 1}>
                            {() => (
                                <StyledMobileHeading>
                                    {selectedShade ? selectedShade.name : selectYourShadeLabel}
                                </StyledMobileHeading>
                            )}
                        </Mobile>
                        {shadeDetails}
                        <Mobile maxWidth={Breakpoints.desktop - 1}>
                            {() => (
                                <ShadePickerWrapper sm={4}>
                                    <ProductShadesSidebarShadePicker
                                        selectedShade={selectedShade}
                                        colorFamilies={colorFamilies}
                                        onShadeSelect={onShadeSelect}
                                        shades={shades}
                                        productId={selectedSku.parentProduct.productId}
                                    />
                                </ShadePickerWrapper>
                            )}
                        </Mobile>
                        {displayInvMessage && (
                            <InventoryStatusMessage
                                inventoryStatus={inventoryStatus}
                                config={config}
                            />
                        )}

                        <Desktop minWidth={Breakpoints.desktop}>
                            {() => (
                                <StyledProductCTA className={shadesSiderbarContentCTAClassNames}>
                                    <ProductCTA
                                        config={desktopCtaConfig}
                                        viewController={diContainer.get(
                                            serviceNames.productCTAViewController
                                        )}
                                    />
                                </StyledProductCTA>
                            )}
                        </Desktop>
                    </ShadeDetails>
                </ShadeInfoWrapper>
                <Desktop minWidth={Breakpoints.desktop}>
                    {() => (
                        <ShadePickerWrapper sm={4}>
                            <ProductShadesSidebarShadePicker
                                selectedShade={selectedShade}
                                colorFamilies={colorFamilies}
                                onShadeSelect={onShadeSelect}
                                shades={shades}
                                productId={selectedSku.parentProduct.productId}
                            />
                        </ShadePickerWrapper>
                    )}
                </Desktop>
                <Mobile maxWidth={Breakpoints.desktop - 1}>
                    {() => (
                        <CtaButtonWrapper>
                            <ProductCTA
                                config={mobileCtaConfig}
                                viewController={diContainer.get(
                                    serviceNames.productCTAViewController
                                )}
                            />
                        </CtaButtonWrapper>
                    )}
                </Mobile>
            </ContentWrapper>
        );
    })
);
